import { Icon } from '@examdojo/core/icon';

export interface CorrelationLibraryDataset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly icon: Icon;
  readonly xLabel: string;
  readonly yLabel: string;
  readonly xValues: readonly number[];
  readonly yValues: readonly number[];
}

export const CORRELATION_LIBRARY_DATASETS: readonly CorrelationLibraryDataset[] = [
  // ── Very strong positive (r ≈ 0.95) ────────────────────────────────
  {
    id: 'armspan-height',
    name: 'Arm Span vs Height',
    description: 'Student arm span and height (cm) — very strong positive',
    icon: 'ruler',
    xLabel: 'Height (cm)',
    yLabel: 'Arm Span (cm)',
    xValues: [152, 155, 158, 160, 163, 165, 167, 168, 170, 172, 174, 175, 177, 178, 180, 182, 184, 186, 188, 191],
    yValues: [149, 158, 154, 164, 159, 168, 162, 172, 166, 174, 170, 179, 173, 181, 176, 184, 180, 189, 185, 193],
  },

  // ── Strong positive (r ≈ 0.90) ─────────────────────────────────────
  {
    id: 'temp-icecream',
    name: 'Temperature vs Ice Cream Sales',
    description: 'Daily temperature (°C) and ice cream revenue — strong positive',
    icon: 'temperature-half',
    xLabel: 'Temperature (°C)',
    yLabel: 'Sales ($)',
    xValues: [8, 10, 12, 14, 16, 18, 20, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 34, 36],
    yValues: [100, 160, 90, 210, 130, 260, 170, 310, 200, 330, 240, 360, 270, 400, 300, 430, 330, 390, 470, 520],
  },

  // ── Strong positive (r ≈ 0.81) ─────────────────────────────────────
  {
    id: 'penguin-flipper-mass',
    name: 'Penguin Flipper Length vs Body Mass',
    description: 'Flipper length (mm) and body mass (g) — strong positive',
    icon: 'kiwi-bird',
    xLabel: 'Flipper Length (mm)',
    yLabel: 'Body Mass (g)',
    xValues: [181, 186, 190, 195, 193, 198, 201, 203, 196, 209, 211, 214, 215, 210, 218, 220, 222, 217, 226, 230],
    yValues: [
      3750, 3800, 4200, 3550, 4600, 4000, 4750, 4200, 4800, 4300, 5100, 5200, 4600, 5500, 4900, 5550, 5100, 5800, 5200,
      5950,
    ],
  },

  // ── Moderate positive (r ≈ 0.71) ───────────────────────────────────
  {
    id: 'gdp-lifeexp',
    name: 'GDP per Capita vs Life Expectancy',
    description: 'National wealth ($1000s) and life expectancy — moderate positive',
    icon: 'coins',
    xLabel: 'GDP per Capita ($1000s)',
    yLabel: 'Life Expectancy (years)',
    xValues: [1.0, 1.8, 3.0, 4.5, 6.0, 8.0, 11, 14, 18, 22, 26, 30, 35, 38, 42, 46, 50, 55, 62, 70],
    yValues: [53, 65, 51, 70, 55, 73, 58, 76, 66, 78, 62, 80, 68, 81, 72, 77, 84, 73, 82, 80],
  },

  // ── Moderate positive (r ≈ 0.70) ───────────────────────────────────
  {
    id: 'study-exam',
    name: 'Study Hours vs Exam Score',
    description: 'Weekly study hours and IB exam results (%) — moderate positive',
    icon: 'graduation-cap',
    xLabel: 'Study Hours',
    yLabel: 'Exam Score (%)',
    xValues: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 16, 17, 18, 19, 20, 22, 25],
    yValues: [32, 65, 40, 58, 72, 45, 68, 50, 75, 55, 82, 48, 78, 70, 90, 60, 85, 72, 88, 92],
  },

  // ── No correlation (r ≈ 0.02) ──────────────────────────────────────
  {
    id: 'shoe-testscore',
    name: 'Shoe Size vs Test Score',
    description: 'Shoe size (UK) and test results (%) — no correlation',
    icon: 'shoe-prints',
    xLabel: 'Shoe Size (UK)',
    yLabel: 'Test Score (%)',
    xValues: [4, 5, 5, 6, 6, 7, 7, 8, 8, 8, 9, 9, 9, 10, 10, 10, 11, 11, 12, 13],
    yValues: [72, 85, 58, 91, 64, 45, 77, 88, 53, 70, 82, 55, 68, 93, 60, 76, 50, 84, 67, 79],
  },

  // ── Moderate negative (r ≈ −0.72) ──────────────────────────────────
  {
    id: 'sleep-reaction',
    name: 'Hours of Sleep vs Reaction Time',
    description: 'Sleep duration and reaction time (ms) — moderate negative',
    icon: 'bed',
    xLabel: 'Hours of Sleep',
    yLabel: 'Reaction Time (ms)',
    xValues: [4, 4.5, 5, 5, 5.5, 6, 6.5, 6.5, 7, 7, 7.5, 7.5, 8, 8, 8.5, 9, 9, 9.5, 10, 10],
    yValues: [420, 310, 400, 340, 250, 370, 260, 350, 300, 220, 330, 200, 280, 190, 270, 240, 170, 280, 190, 210],
  },

  // ── Very strong negative (r ≈ −0.96) ───────────────────────────────
  {
    id: 'latitude-temperature',
    name: 'Latitude vs Mean Temperature',
    description: 'Latitude (°N) and mean annual temperature (°C) — very strong negative',
    icon: 'earth-americas',
    xLabel: 'Latitude (°N)',
    yLabel: 'Mean Temperature (°C)',
    xValues: [1, 6, 10, 14, 19, 23, 30, 34, 38, 41, 45, 48, 51, 53, 56, 59, 61, 64, 67, 71],
    yValues: [27, 25, 28, 23, 25, 18, 22, 15, 19, 12, 13, 8, 11, 6, 4, 8, 1, 2, -2, -5],
  },
];
